import type { ReactElement, ReactNode } from 'react';
import { Fragment, useContext } from 'react';

import { t } from '@luna/i18n';
import {
    getFormattedDateRange,
    getFormattedRange,
    getProductsCountFilterMessage,
} from '@shared/transactions-utils';
import type { Card } from '@shared/cards-types';

import type { SearchState } from './types';
import { DirectionFilter } from './types';
import { useSearchStateFromUrl } from './useSearchStateFromUrl';
import type { CardRenderer } from './ProductsRenderingContext';
import { ProductsRenderingContext } from './ProductsRenderingContext';

interface SearchCriteria {
    criteria: ReactElement[];
}

export function useSearchCriteria(
    overrideSearchStateFromUrl?: Partial<SearchState>,
): SearchCriteria {
    const searchState = useSearchStateFromUrl();
    const { maskedCardNumber: cardRenderer } = useContext(ProductsRenderingContext);

    const {
        keyword = '',
        dates = {},
        amount = {},
        billIdRange = {},
        direction,
        cardIds,
        maskedCardNumber,
        variableSymbol,
        accounts,
    } = { ...searchState, ...overrideSearchStateFromUrl };

    const { startDate, endDate } = dates;
    const { minAmount, maxAmount } = amount;
    const { billIdFrom, billIdTo } = billIdRange;

    const filterStrings = [
        {
            render: () => keyword,
            shouldShow: !!keyword,
            key: 'keyword',
        },
        {
            render: () => getFilterDirectionText(direction),
            shouldShow: !!direction,
            key: 'direction',
        },
        {
            render: () => getFormattedDateRange(startDate, endDate),
            shouldShow: !!startDate || !!endDate,
            key: 'date',
        },
        {
            render: () => getFormattedRange(minAmount, maxAmount),
            shouldShow: !!minAmount || !!maxAmount,
            key: 'amount',
        },
        {
            render: () => getFormattedRange(billIdFrom, billIdTo),
            shouldShow: Boolean(billIdTo || billIdFrom),
            key: 'billId',
        },
        {
            render: () => getCardsById(cardIds ?? [], cardRenderer),
            shouldShow: Boolean(cardIds?.length),
            key: 'cardId',
        },
        {
            render: () => getMaskedCardNumberText(maskedCardNumber ?? []),
            shouldShow: Boolean(maskedCardNumber?.length),
            key: 'maskedCardNumber',
        },
        {
            render: () => variableSymbol,
            shouldShow: !!variableSymbol,
            key: 'variableSymbol',
        },
        {
            render: () => getProductsCountFilterMessage(accounts?.length),
            shouldShow: Boolean(accounts?.length),
            key: 'products',
        },
    ];

    const criteria = filterStrings
        .filter(({ shouldShow }) => shouldShow)
        .map(({ key, render }) => <Fragment key={key}>{render()}</Fragment>);

    return { criteria };
}

function getCardsById(cardIds: Array<Card['id']>, Renderer?: CardRenderer): ReactNode {
    const { length: count } = cardIds;

    if (Renderer && count === 1) {
        const [cardId] = cardIds;
        return <Renderer cardId={cardId} />;
    }

    return t('print.filterCardsSelected', { count });
}

function getMaskedCardNumberText(maskedCardNumbers: Array<string>): string {
    const { length: count } = maskedCardNumbers;
    const [maskedCardNumber] = maskedCardNumbers;
    return count === 1
        ? t('print.filterSingleCardSelected').replace('{maskedCardNumber}', maskedCardNumber)
        : t('print.filterCardsSelected', { count });
}

function getFilterDirectionText(direction?: DirectionFilter): string {
    if (direction === DirectionFilter.INCOMING) {
        return t('search.searchFilters.incoming');
    }
    if (direction === DirectionFilter.OUTGOING) {
        return t('search.searchFilters.outgoing');
    }
    return '';
}
